/**
 * @module app.shared
 */

import { encodeQueryString } from 'app/shared/lib/utilities';
import { ApiDownloadDisposition, ApiResponseType, IApiRequest } from 'app/shared/lib/api/api-types';

interface IDownloadInternals {
    tenant: string | null;
    url: string | null;
    disposition: ApiDownloadDisposition;
    scope: string | null;
}

/**
 * API Download Builder
 */
export class ApiDownload implements IApiRequest {

    private readonly internals: IDownloadInternals = {
        tenant: null,
        url: null,
        disposition: 'attachment',
        scope: null
    };

    public readonly responseType: ApiResponseType = 'blob';

    /**
     * Construct a new API Settings object
     *
     * **Note**: `api.settings()` is a shortcut to constructing this object
     * @param url the API URL for which to load settings
     */
    constructor(url: string) {
        this.internals.url = url;
    }

    /** Returns `true` if this settings request has a tenant code value specified */
    public hasTenant(): boolean {
        return !!this.internals.tenant;
    }

    /**
     * Specify the tenant code to use for this settings request
     * @param tenant the tenant code to settings request, or `null` to use the currently logged in tenant
     */
    public withTenant(tenant: string | null): this {
        this.internals.tenant = tenant;
        return this;
    }

    public withDisposition(disposition: ApiDownloadDisposition): this {
        this.internals.disposition = disposition;
        return this;
    }

    /**
     * Override the URL used to make this settings request
     * @param url the new URL to settings request against
     */
    public overrideUrl(url: string): this {
        this.internals.url = url;
        return this;
    }

    /**
     * Convert the current settings request to an API URL
     */
    public toString(): string {
        if(!this.internals.url) {
            return '';
        }

        const slash = this.internals.url.endsWith('/') ? '' : '/';
        const url = this.internals.tenant ? `${encodeURI(this.internals.tenant)}/${this.internals.url}${slash}!download` : this.internals.url;
        const query: string[] = [];

        query.push(`disposition=${this.internals.disposition}`);
        if(this.internals.scope) {
            query.push(`scope=${encodeQueryString(this.internals.scope)}`);
        }
        return (query.length > 0 ? (`${url}?${query.join('&')}`) : url);
    }

}
